/**
 * Marquee-drag selection demo.
 *
 * Replaces the per-node halo idiom on steps where the user is meant to
 * marquee-select one or more canvas nodes. Renders, as a screen-fixed
 * portal:
 *
 *  • A virtual mouse cursor that animates in from outside the target
 *    bounding box, presses down on the upper-left corner, drags
 *    diagonally to the lower-right corner, releases, fades, and resets.
 *  • A translucent rust-tinted rectangle that grows in lockstep with
 *    the cursor's drag — the same gesture a user would make to draw a
 *    marquee around the targets.
 *
 * The union bounding rect of the supplied `nodeIds` is re-measured
 * every animation frame so the demo tracks canvas pan/zoom. Bounding
 * geometry is fed into the CSS keyframes via custom properties
 * (`--marquee-w`, `--marquee-h`), avoiding any need to ship one keyframe
 * per possible bbox size.
 *
 * The component renders nothing until at least one of the targets is
 * present in the DOM. Auto-pauses (no animation) when none are.
 */

import { memo, useEffect, useState } from "react";
import { createPortal } from "react-dom";

function cssEscape(s: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(s);
  }
  return s.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}

/** Visual breathing room added on every side of the union bbox so the
 *  marquee starts and ends just outside the nodes themselves — same as
 *  a user would naturally drag to avoid grabbing a node directly. */
const MARQUEE_PAD = 28;

const MARQUEE_STYLES = `
  /* Cursor: enters from offset, glides onto top-left of the union
     bbox (click), then drags to bottom-right (offset by --marquee-w /
     --marquee-h), releases, fades, resets. */
  @keyframes _co_onb_marquee_cursor {
    0%   { transform: translate(-32px, -32px) scale(1);    opacity: 0; }
    6%   { transform: translate(-32px, -32px) scale(1);    opacity: 1; }
    14%  { transform: translate(0px, 0px) scale(1);        opacity: 1; }
    18%  { transform: translate(0px, 0px) scale(0.86);     opacity: 1; }
    62%  { transform: translate(var(--marquee-w, 0px), var(--marquee-h, 0px)) scale(0.86); opacity: 1; }
    68%  { transform: translate(var(--marquee-w, 0px), var(--marquee-h, 0px)) scale(1);    opacity: 1; }
    82%  { transform: translate(var(--marquee-w, 0px), var(--marquee-h, 0px)) scale(1);    opacity: 0; }
    100% { transform: translate(-32px, -32px) scale(1);    opacity: 0; }
  }
  ._co_onb_marquee_cursor {
    position: fixed;
    width: 42px;
    height: 42px;
    pointer-events: none;
    /* Theme-aware: dark cursor on light canvas, light cursor on dark
       canvas, with opposite-tone stroke so the outline always reads. */
    color: #1A1A16;
    --co-cursor-stroke: #F3EFE6;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.22));
    z-index: 52;
    animation: _co_onb_marquee_cursor 3.8s cubic-bezier(0.45, 0, 0.2, 1) infinite;
  }
  .dark ._co_onb_marquee_cursor {
    color: #F3EFE6;
    --co-cursor-stroke: #1A1A16;
  }

  /* Marquee rectangle — grows from 0×0 in lockstep with the cursor's
     diagonal drag (~18→62% of the cycle), holds, then fades. */
  @keyframes _co_onb_marquee_rect {
    0%, 14% { width: 0; height: 0; opacity: 0; }
    18%     { width: 0; height: 0; opacity: 1; }
    62%     {
      width: var(--marquee-w, 0px);
      height: var(--marquee-h, 0px);
      opacity: 1;
    }
    76%     {
      width: var(--marquee-w, 0px);
      height: var(--marquee-h, 0px);
      opacity: 0;
    }
    100%    { width: 0; height: 0; opacity: 0; }
  }
  ._co_onb_marquee_rect {
    position: fixed;
    border: 1.5px dashed #B86E49;
    background: rgba(184, 110, 73, 0.10);
    border-radius: 6px;
    pointer-events: none;
    opacity: 0;
    z-index: 51;
    animation: _co_onb_marquee_rect 3.8s cubic-bezier(0.45, 0, 0.2, 1) infinite;
    box-sizing: border-box;
  }

  @media (prefers-reduced-motion: reduce) {
    ._co_onb_marquee_cursor,
    ._co_onb_marquee_rect { animation: none !important; }
  }
`;

/** Classic arrow cursor glyph, black-outlined so it reads on both
 *  light and dark canvas backgrounds. Tip anchored at path (5, 3). */
function MarqueeCursorGlyph() {
  return (
    <svg
      width="42"
      height="42"
      viewBox="0 0 28 28"
      fill="none"
      style={{ display: "block" }}
      aria-hidden
    >
      <path
        d="M5 3 L5 22 L10.5 17.5 L13.5 24.5 L16.5 23.2 L13.5 16.2 L21 16.2 Z"
        fill="currentColor"
        stroke="var(--co-cursor-stroke, #ffffff)"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface BBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

function computeUnionBBox(nodeIds: string[]): BBox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let found = false;

  for (const id of nodeIds) {
    const el = document.querySelector<HTMLElement>(
      `.react-flow__node[data-id="${cssEscape(id)}"]`,
    );
    if (!el) continue;
    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    minX = Math.min(minX, r.left);
    minY = Math.min(minY, r.top);
    maxX = Math.max(maxX, r.right);
    maxY = Math.max(maxY, r.bottom);
    found = true;
  }

  if (!found) return null;
  return {
    left: minX - MARQUEE_PAD,
    top: minY - MARQUEE_PAD,
    width: maxX - minX + MARQUEE_PAD * 2,
    height: maxY - minY + MARQUEE_PAD * 2,
  };
}

export const MarqueeSelectDemo = memo(function MarqueeSelectDemo({
  nodeIds,
}: {
  nodeIds: string[];
}) {
  const [bbox, setBBox] = useState<BBox | null>(null);

  // Re-measure every frame so the demo tracks canvas pan/zoom and any
  // node settle/resize transitions. The DOM reads are cheap (a handful
  // of getBoundingClientRect calls); React only re-renders on actual
  // value change because setState is no-op for equal references — we
  // explicitly avoid setBBox unless geometry actually shifted.
  useEffect(() => {
    if (nodeIds.length === 0) {
      setBBox(null);
      return;
    }
    let raf = 0;
    let lastSerialized = "";
    const loop = () => {
      const next = computeUnionBBox(nodeIds);
      const serialized = next
        ? `${next.left}|${next.top}|${next.width}|${next.height}`
        : "";
      if (serialized !== lastSerialized) {
        lastSerialized = serialized;
        setBBox(next);
      }
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(raf);
  }, [nodeIds]);

  if (!bbox) {
    // Style block still gets injected so a brief node-mount race
    // doesn't leave the keyframes undefined when the bbox resolves a
    // tick later.
    return createPortal(<style>{MARQUEE_STYLES}</style>, document.body);
  }

  // Cursor tip is at SVG path (5, 3); offset the element so the tip
  // lands on the top-left corner of the bbox when keyframe translate
  // is (0, 0). The drag offset (`--marquee-w/h`) then carries the tip
  // to the bottom-right corner.
  const cursorLeft = bbox.left - 5;
  const cursorTop = bbox.top - 3;
  const cssVars = {
    ["--marquee-w" as string]: `${bbox.width}px`,
    ["--marquee-h" as string]: `${bbox.height}px`,
  } as React.CSSProperties;

  return createPortal(
    <>
      <style>{MARQUEE_STYLES}</style>
      <div
        aria-hidden
        className="_co_onb_marquee_rect"
        style={{ ...cssVars, left: bbox.left, top: bbox.top }}
      />
      <span
        aria-hidden
        className="_co_onb_marquee_cursor"
        style={{ ...cssVars, left: cursorLeft, top: cursorTop }}
      >
        <MarqueeCursorGlyph />
      </span>
    </>,
    document.body,
  );
});
